import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import stageIcon from "../../Assets/Images/mnt-podsused-logo.png";
import LoaderBox from "./LoaderBox";
import BottomNavigation from "../Navigation/BottomNavigation";
import Navigation from "../Navigation/Navigation";
import HomeSponsors from "./HomeSponsors";
import HomeFooter from "./HomeFooter";

const HomeChampionship = () => {
  const { turnirSlug } = useParams();
  const [activeTab, setActiveTab] = useState("seniori");
  const [hasVeterani, setHasVeterani] = useState(false);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hideDataMode, setHideDataMode] = useState(false);
  const [currentTurnirId, setCurrentTurnirId] = useState(null);
  const [seniorSize, setSeniorSize] = useState(16);
  const [veteranSize, setVeteranSize] = useState(4);
  const championshipSize = activeTab === "veterani" ? veteranSize : seniorSize;
  const [turnirInfo, setTurnirInfo] = useState({
    naziv: "",
    logo_url: "",
    lokacija: "",
    naziv_lokacije: "",
    features: {},
  });

  const fetchBracket = useCallback(async () => {
    if (!turnirSlug) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const { data: turnirData, error: turnirError } = await supabase
        .from("admin_turniri")
        .select(
          "id, naziv, logo_url, lokacija, naziv_lokacije, features, championship_size",
        )
        .eq("slug", turnirSlug)
        .single();

      if (turnirError) throw turnirError;

      if (turnirData) {
        setCurrentTurnirId(turnirData.id);
        setSeniorSize(turnirData.championship_size || 16);
        setVeteranSize(
          turnirData.championship_size_veterani ||
            turnirData.features?.championship_size_veterani ||
            4,
        );
        setHasVeterani(turnirData.features?.veterani === true);
        setTurnirInfo({
          naziv: turnirData.naziv || "",
          logo_url: turnirData.logo_url || "",
          lokacija: turnirData.lokacija || "",
          naziv_lokacije: turnirData.naziv_lokacije || "",
          features: turnirData.features || {},
        });

        const { data: settings } = await supabase
          .from("site_settings")
          .select("hide_data_mode")
          .eq("id", 1)
          .maybeSingle();
        if (settings) setHideDataMode(settings.hide_data_mode || false);

        // Ovisno o aktivnom tabu biramo pravu tablicu
        const targetTable =
          activeTab === "veterani" ? "turniri_veterani" : "turniri_utakmice";

        const { data: matchesData, error: matchesError } = await supabase
          .from(targetTable)
          .select(
            `
            *,
            team_a:team_a_id(name),
            team_b:team_b_id(name)
          `,
          )
          .eq("tournament_id", turnirData.id)
          .eq("group_name", "Završnica");

        if (matchesError) {
          console.error("Greška pri dohvatu utakmica:", matchesError);
        } else {
          setMatches(matchesData || []);
        }
      }
    } catch (error) {
      console.error("Greška pri dohvatu podataka završnice:", error.message);
    } finally {
      setLoading(false);
    }
  }, [turnirSlug, activeTab]);

  useEffect(() => {
    fetchBracket();

    const targetTable =
      activeTab === "veterani" ? "turniri_veterani" : "turniri_utakmice";
    const subscription = supabase
      .channel(`championship_changes_${activeTab}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: targetTable },
        () => {
          fetchBracket();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [fetchBracket, activeTab]);

  const getMatch = (slotId) => {
    if (hideDataMode) {
      return {
        team_a: { name: "?" },
        team_b: { name: "?" },
        scorea: 0,
        scoreb: 0,
      };
    }

    const match = matches.find((m) => m.bracket_slot_id === slotId);
    return (
      match || {
        team_a: { name: "?" },
        team_b: { name: "?" },
        scorea: 0,
        scoreb: 0,
      }
    );
  };

  const getWinner = () => {
    if (hideDataMode) return "?";
    // Točan ID finala: za 16 timova je 15, za 8 timova je 7, za 4 tima je 3
    const finalSlotId =
      championshipSize === 16 ? 15 : championshipSize === 8 ? 7 : 3;
    const finale = matches.find((m) => m.bracket_slot_id === finalSlotId);

    if (!finale || !finale.is_completed) return "?";
    if (finale.scorea > finale.scoreb) return finale.team_a?.name || "?";
    if (finale.scoreb > finale.scorea) return finale.team_b?.name || "?";
    return "?";
  };

  if (loading) {
    return (
      <>
        <Navigation
          turnirSlug={turnirSlug}
          turnirNaziv={turnirInfo.naziv}
          turnirLogo={turnirInfo.logo_url}
          lokacija={turnirInfo.lokacija}
          nazivLokacije={turnirInfo.naziv_lokacije}
        />
        <div className="championship-wrapper">
          <LoaderBox text="Učitavanje završnice..." />
        </div>
        <BottomNavigation />
      </>
    );
  }

  return (
    <>
      <Navigation
        turnirSlug={turnirSlug}
        turnirNaziv={turnirInfo.naziv}
        turnirLogo={turnirInfo.logo_url}
        lokacija={turnirInfo.lokacija}
        nazivLokacije={turnirInfo.naziv_lokacije}
        features={turnirInfo.features}
      />

      <div className={`championship-wrapper bracket-size-${championshipSize}`}>
        <div className="championship-stage-header">
          <div>
            {hasVeterani && (
              <div
                className="category-switcher-bar"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                  marginBottom: "10px",
                }}
              >
                <button
                  className={`switcher-btn ${activeTab === "seniori" ? "active" : ""}`}
                  onClick={() => setActiveTab("seniori")}
                >
                  Seniori
                </button>
                <button
                  className={`switcher-btn ${activeTab === "veterani" ? "active" : ""}`}
                  onClick={() => setActiveTab("veterani")}
                >
                  Veterani
                </button>
              </div>
            )}
          </div>
          <img src={stageIcon} alt="Stage" className="stage-img" />
          <h3>
            Završnica Turnira (
            {activeTab === "veterani" ? "Veterani" : "Seniori"})
          </h3>
        </div>

        <div className="championship-scroll">
          {/* 1. STUPAC: Osmina finala (samo za 16 timova) */}
          {championshipSize === 16 && (
            <div className="championship-column stage-0">
              <h4 className="stage-title">Osmina finala</h4>
              <div className="matches-grid">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((id) => {
                  const m = getMatch(id);
                  return (
                    <div key={id} className="championship-match">
                      <div className="team">
                        <span>{m.team_a?.name || m.placeholder_a || "?"}</span>
                        <span className="score">{m.scorea}</span>
                      </div>
                      <div className="team">
                        <span>{m.team_b?.name || m.placeholder_b || "?"}</span>
                        <span className="score">{m.scoreb}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 2. STUPAC: Četvrtfinale (za 16 ili 8 timova) */}
          {(championshipSize === 16 || championshipSize === 8) && (
            <div
              className={`championship-column ${championshipSize === 8 ? "stage-0" : "stage-1"}`}
            >
              <h4 className="stage-title">Četvrtfinale</h4>
              <div className="matches-grid">
                {(championshipSize === 16 ? [9, 10, 11, 12] : [1, 2, 3, 4]).map(
                  (id) => {
                    const m = getMatch(id);
                    return (
                      <div key={id} className="championship-match">
                        <div className="team">
                          <span>
                            {m.team_a?.name || m.placeholder_a || "?"}
                          </span>
                          <span className="score">{m.scorea}</span>
                        </div>
                        <div className="team">
                          <span>
                            {m.team_b?.name || m.placeholder_b || "?"}
                          </span>
                          <span className="score">{m.scoreb}</span>
                        </div>
                        <div className="vertical-line"></div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          )}

          {/* 3. STUPAC: Polufinale (za 16, 8 i 4 tima) */}
          <div
            className={`championship-column ${championshipSize === 4 ? "stage-0" : championshipSize === 8 ? "stage-1" : "stage-2"}`}
          >
            <h4 className="stage-title">Polufinale</h4>
            <div className="matches-grid">
              {(championshipSize === 16
                ? [13, 14]
                : championshipSize === 8
                  ? [5, 6]
                  : [1, 2]
              ).map((id) => {
                const m = getMatch(id);
                return (
                  <div key={id} className="championship-match">
                    <div className="team">
                      <span>{m.team_a?.name || m.placeholder_a || "?"}</span>
                      <span className="score">{m.scorea}</span>
                    </div>
                    <div className="team">
                      <span>{m.team_b?.name || m.placeholder_b || "?"}</span>
                      <span className="score">{m.scoreb}</span>
                    </div>
                    {championshipSize !== 4 && (
                      <div className="vertical-line"></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 4. STUPAC: Finale, Trofej i 3. mjesto */}
          <div className="championship-column stage-3 final-stage">
            <h4 className="stage-title">Finale</h4>
            <div className="final-content-wrapper">
              {(() => {
                const finalSlotId = championshipSize === 4 ? 3 : 15;
                const m = getMatch(finalSlotId);
                return (
                  <div className="championship-match finale-box">
                    <div className="team">
                      <span>{m.team_a?.name || m.placeholder_a || "?"}</span>
                      <span className="score">{m.scorea}</span>
                    </div>
                    <div className="team">
                      <span>{m.team_b?.name || m.placeholder_b || "?"}</span>
                      <span className="score">{m.scoreb}</span>
                    </div>
                    <div className="vertical-line"></div>
                  </div>
                );
              })()}

              <div className="trophy-container">
                <img src={stageIcon} alt="Stage" className="stage-img" />
                <h3>POBJEDNIK</h3>
                <div className="winner-box">{getWinner()}</div>
              </div>

              {/* 3. MJESTO */}
              <div className="third-place-container">
                <h4 className="stage-title">3. MJESTO</h4>
                <div className="matches-grid">
                  {(() => {
                    const thirdPlaceSlotId = championshipSize === 4 ? 4 : 16;
                    const m = getMatch(thirdPlaceSlotId);
                    return (
                      <div className="championship-match">
                        <div className="team">
                          <span>
                            {m.team_a?.name || m.placeholder_a || "?"}
                          </span>
                          <span className="score">{m.scorea}</span>
                        </div>
                        <div className="team">
                          <span>
                            {m.team_b?.name || m.placeholder_b || "?"}
                          </span>
                          <span className="score">{m.scoreb}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="scroll-hint">
          <div className="line"></div>
          <span>Povuci za faze</span>
          <div className="line"></div>
        </div>
      </div>
      <HomeSponsors turnirId={currentTurnirId} />
      <HomeFooter />
      <BottomNavigation />
    </>
  );
};

export default HomeChampionship;
